// components/project/economic/EconomicUsciteView.jsx
// V1.1 Step 3 — Vista Uscite operativa
//
// Fonti dati (già disponibili, no backend nuovo):
//   - /api/projects/{id}/economic-lines           (righe Q.E. con supplier, assigned_cost, actual_cost)
//   - /api/projects/{id}/economic-lines/{id}/installments  (rate per line, in parallelo)
//   - /api/subcontracts?project_id={id}           (subappalti collegati alle line via economic_line_id)
//
// Regola dati onesta:
//   Affidato       = economic_line.assigned_cost (V1 Fase C)
//   Consuntivato   = economic_line.actual_cost
//   Pagato         = somma installments.paid_amount della line (se rate disponibili)
//   Residuo        = calcolato solo se pagato > 0 O assigned_cost > 0 (max(assigned, actual) - pagato)
//   Se nessun dato: '—' o hint

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Search, ShoppingCart, FileText, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
};

const SUPPLIER_TYPE_LABELS = {
  subappaltatore_edile: 'Subappaltatore edile',
  elettricista: 'Elettricista',
  idraulico: 'Idraulico',
  serramentista: 'Serramentista',
  fornitore_materiali: 'Fornitore materiali',
  fornitore_arredi: 'Fornitore arredi',
  studio_tecnico_esterno: 'Studio tecnico esterno',
  professionista: 'Professionista',
  altro: 'Altro',
  // legacy
  edile: 'Subappaltatore edile',
  elettrico: 'Elettricista',
  serramenti: 'Serramentista',
};

const LINE_STATUS_META = {
  bozza:      { label: 'Bozza',      color: 'bg-slate-100 text-slate-700 border-slate-200' },
  confermato: { label: 'Confermato', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  in_corso:   { label: 'In corso',   color: 'bg-blue-100 text-blue-800 border-blue-200' },
  fatturato:  { label: 'Fatturato',  color: 'bg-purple-100 text-purple-800 border-purple-200' },
  chiuso:     { label: 'Chiuso',     color: 'bg-slate-100 text-slate-500 border-slate-200' },
  annullato:  { label: 'Annullato',  color: 'bg-rose-100 text-rose-700 border-rose-200' },
};

const EconomicUsciteView = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availableTypes, setAvailableTypes] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1) economic-lines (base)
        const linesRes = await axios.get(`${API}/api/projects/${projectId}/economic-lines`);
        if (cancelled) return;
        const lines = Array.isArray(linesRes.data) ? linesRes.data : (linesRes.data?.lines || linesRes.data?.items || []);

        // QE V2: escludi le righe DETAIL figlie (parent_economic_line_id) per non duplicare i costi
        const relevant = lines.filter(l => !l.parent_economic_line_id && ((l.supplier_name && l.supplier_name.trim()) || (l.assigned_cost || 0) > 0 || (l.actual_cost || 0) > 0));

        // 3) fetch installments summary per ogni line (parallel)
        const summaries = await Promise.all(relevant.map(async (l) => {
          try {
            const r = await axios.get(`${API}/api/projects/${projectId}/economic-lines/${l.id}/installments`);
            return { line_id: l.id, summary: r.data?.summary || {} };
          } catch {
            return { line_id: l.id, summary: {} };
          }
        }));
        if (cancelled) return;
        const summaryMap = Object.fromEntries(summaries.map(s => [s.line_id, s.summary]));

        // 4) fetch subappalti del progetto per link contratto (se disponibile)
        //    Il campo economic_line_id sul subcontract è opzionale. Fallback: match per supplier_id.
        let subByLineId = {};
        let subBySupplierId = {};
        try {
          const subRes = await axios.get(`${API}/api/subcontracts?project_id=${projectId}`);
          const subs = subRes.data?.items || subRes.data || [];
          for (const s of subs) {
            if (s.soft_deleted) continue;
            if (s.economic_line_id) subByLineId[s.economic_line_id] = s;
            if (s.supplier_id) {
              // Preferisci sub in stato firmato/chiuso su stessa supplier
              const prev = subBySupplierId[s.supplier_id];
              const rank = { firmato: 3, chiuso: 2, pronto_per_firma: 1, bozza: 0, annullato: -1 };
              if (!prev || (rank[s.status] || 0) > (rank[prev.status] || 0)) {
                subBySupplierId[s.supplier_id] = s;
              }
            }
          }
        } catch { /* non bloccante */ }

        // 5) Build rows
        const newRows = relevant.map((l) => {
          const s = summaryMap[l.id] || {};
          // Priorità: match diretto per economic_line_id, poi fallback per supplier_id
          const sub = subByLineId[l.id] || (l.supplier_id ? subBySupplierId[l.supplier_id] : null);
          const pagato = Number(s.installments_paid_total || 0);
          const assigned = Number(l.assigned_cost || 0);
          const actual = Number(l.actual_cost || 0);
          const hasInstallments = Number(s.installments_count || 0) > 0;
          const hasAffidato = assigned > 0;
          const hasConsuntivo = actual > 0;
          // Residuo: calcolabile solo se abbiamo un impegno (affidato o consuntivo)
          const base = Math.max(assigned, actual);
          const residuo = (hasAffidato || hasConsuntivo) ? Math.max(base - pagato, 0) : null;

          return {
            id: l.id,
            fornitore: l.supplier_name || '—',
            supplierId: l.supplier_id || null,
            tipologiaKey: l.supplier_category || null,
            tipologia: l.supplier_category ? (SUPPLIER_TYPE_LABELS[l.supplier_category] || l.supplier_category) : '—',
            categoriaLavori: l.category || '—',
            contratto: sub ? sub.subcontract_number : (l.reference_document || null),
            subcontractId: sub?.id || null,
            affidato: hasAffidato ? assigned : null,
            consuntivato: hasConsuntivo ? actual : null,
            pagato: hasInstallments ? pagato : null,
            residuo,
            stato: l.status || 'bozza',
            docCount: Number(s.installments_count || 0),
          };
        });

        setRows(newRows);
        // Tipologie disponibili (per filtro dinamico)
        const types = Array.from(new Set(newRows.map(r => r.tipologiaKey).filter(Boolean)));
        setAvailableTypes(types);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Errore caricamento Uscite');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (projectId) load();
    return () => { cancelled = true; };
  }, [projectId]);

  const filteredRows = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter(r => {
      if (typeFilter !== 'all' && r.tipologiaKey !== typeFilter) return false;
      if (statusFilter !== 'all' && r.stato !== statusFilter) return false;
      if (query && !`${r.fornitore} ${r.categoriaLavori} ${r.contratto || ''}`.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [rows, q, typeFilter, statusFilter]);

  const totals = useMemo(() => {
    const t = { affidato: 0, consuntivato: 0, pagato: 0, residuo: 0 };
    let hasResiduo = false;
    for (const r of filteredRows) {
      if (r.affidato != null) t.affidato += r.affidato;
      if (r.consuntivato != null) t.consuntivato += r.consuntivato;
      if (r.pagato != null) t.pagato += r.pagato;
      if (r.residuo != null) { t.residuo += r.residuo; hasResiduo = true; }
    }
    t.hasResiduo = hasResiduo;
    return t;
  }, [filteredRows]);

  if (loading) {
    return <div className="p-8 text-center text-sm text-[#737373]" data-testid="qe-uscite-loading">Caricamento uscite...</div>;
  }
  if (error) {
    return <Card className="card-abitalo"><CardContent className="p-6 text-center text-sm text-rose-600">{error}</CardContent></Card>;
  }
  if (rows.length === 0) {
    return (
      <Card className="card-abitalo" data-testid="qe-uscite-empty">
        <CardContent className="p-8 text-center">
          <ShoppingCart className="w-10 h-10 text-[#a3a3a3] mx-auto mb-3" />
          <h4 className="text-base font-semibold text-[#3C3C3C]">Nessuna uscita registrata</h4>
          <p className="text-sm text-[#737373] mt-1 max-w-md mx-auto">
            Le uscite compariranno quando avrai voci Q.E. con fornitore assegnato, subappalti firmati o rate inserite.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="qe-uscite-view">
      {/* Filtri */}
      <div className="flex gap-2 flex-wrap" data-testid="qe-uscite-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <Input placeholder="Cerca fornitore, categoria, contratto..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="qe-uscite-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-56" data-testid="qe-uscite-type-filter" disabled={availableTypes.length === 0}><SelectValue placeholder={availableTypes.length === 0 ? 'Tipologia n.d.' : 'Tipologia fornitore'} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le tipologie</SelectItem>
            {availableTypes.map(t => (
              <SelectItem key={t} value={t}>{SUPPLIER_TYPE_LABELS[t] || t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="qe-uscite-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(LINE_STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tabella */}
      <Card className="card-abitalo">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qe-uscite-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[#525252] text-xs uppercase">
                  <th className="px-3 py-2 font-semibold">Fornitore</th>
                  <th className="px-3 py-2 font-semibold">Tipologia</th>
                  <th className="px-3 py-2 font-semibold">Cat. lavori</th>
                  <th className="px-3 py-2 font-semibold">Contratto</th>
                  <th className="px-3 py-2 font-semibold text-right">Affidato</th>
                  <th className="px-3 py-2 font-semibold text-right">Consuntivato</th>
                  <th className="px-3 py-2 font-semibold text-right">Pagato</th>
                  <th className="px-3 py-2 font-semibold text-right">Residuo</th>
                  <th className="px-3 py-2 font-semibold">Stato</th>
                  <th className="px-3 py-2 font-semibold">Doc.</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const st = LINE_STATUS_META[r.stato] || { label: r.stato, color: 'bg-slate-100 text-slate-700 border-slate-200' };
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`qe-uscite-row-${r.id}`}>
                      <td className="px-3 py-2 font-medium">{r.fornitore}</td>
                      <td className="px-3 py-2 text-xs text-[#525252]">{r.tipologia}</td>
                      <td className="px-3 py-2 text-xs text-[#525252]">{r.categoriaLavori}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.contratto ? (
                          <span className="inline-flex items-center gap-1 text-[#525252] font-mono">
                            <FileText className="w-3 h-3" />{r.contratto}
                          </span>
                        ) : <span className="text-[#a3a3a3]">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        {r.affidato != null ? <span className="text-[#FF7819]">{fmtEur(r.affidato)}</span> : <span className="text-[#a3a3a3]" title="Disponibile dopo firma subappalto">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.consuntivato != null ? fmtEur(r.consuntivato) : <span className="text-[#a3a3a3]" title="Disponibile dopo consuntivo/fatture fornitori (V2)">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.pagato != null ? fmtEur(r.pagato) : <span className="text-[#a3a3a3]" title="Disponibile dopo inserimento rate/pagamenti">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.residuo != null ? (
                          <span className={r.residuo > 0 ? 'text-rose-600 font-medium' : 'text-emerald-700 font-medium'}>
                            {fmtEur(r.residuo)}
                          </span>
                        ) : <span className="text-[#a3a3a3]" title="Calcolabile dopo affidamento o consuntivo">—</span>}
                      </td>
                      <td className="px-3 py-2"><Badge className={`text-[10px] border ${st.color}`}>{st.label}</Badge></td>
                      <td className="px-3 py-2 text-center text-xs text-[#737373]">{r.docCount || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-orange-50/50 border-t-2 border-orange-200 font-semibold text-[#B25400]">
                <tr data-testid="qe-uscite-totals">
                  <td className="px-3 py-2" colSpan={4}>Totali (filtrati: {filteredRows.length}/{rows.length})</td>
                  <td className="px-3 py-2 text-right">{fmtEur(totals.affidato)}</td>
                  <td className="px-3 py-2 text-right">{totals.consuntivato > 0 ? fmtEur(totals.consuntivato) : '—'}</td>
                  <td className="px-3 py-2 text-right">{totals.pagato > 0 ? fmtEur(totals.pagato) : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    {totals.hasResiduo ? (
                      <span className={totals.residuo > 0 ? 'text-rose-700' : 'text-emerald-700'}>{fmtEur(totals.residuo)}</span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2" colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="text-[11px] text-[#a3a3a3] italic px-1 pt-1 flex items-start gap-1">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          Fonti: righe Q.E. con fornitore/affidato (assigned_cost da subappalti firmati) · Rate per line · Subappalti collegati.
          Pagato/Residuo mostrati solo se realmente disponibili. Fatture fornitori (consuntivo automatico) in V2.
        </span>
      </div>
    </div>
  );
};

export default EconomicUsciteView;
